<script>
  export let stats;
  export let compact = false;

  const RANKS = [
    { name: "Puzzler", min: 0, max: 14, stars: 1 },
    { name: "Hobbyist", min: 15, max: 49, stars: 2 },
    { name: "Curator", min: 50, max: 149, stars: 3 },
    { name: "Connoisseur", min: 150, max: 299, stars: 4 },
    { name: "Archivist", min: 300, max: Infinity, stars: 5 },
  ];

  $: rank = getRank(stats.unique_puzzles);
  $: rankIndex = RANKS.indexOf(rank);
  $: nextRank = rankIndex < RANKS.length - 1 ? RANKS[rankIndex + 1] : null;
  $: maxBrandCount = stats.top_brands?.length
    ? Math.max(...stats.top_brands.map((b) => b.count))
    : 1;
  $: maxCatCount = stats.top_categories?.length
    ? Math.max(...stats.top_categories.map((c) => c.count))
    : 1;

  function getRank(puzzles) {
    for (let i = RANKS.length - 1; i >= 0; i--) {
      if (puzzles >= RANKS[i].min) return RANKS[i];
    }
    return RANKS[0];
  }

  function formatNumber(n) {
    return n?.toLocaleString() ?? "0";
  }
</script>

<div class="puzzle-stats" class:compact>

  <!-- ── Rank timeline ─────────────────────────────────────────────── -->
  <div class="rank-timeline">
    <div class="timeline-header">
      <div class="rank-label">Your Rank</div>
      <div class="rank-name">{rank.name}</div>
      {#if nextRank}
        <div class="rank-next">
          {nextRank.min - stats.unique_puzzles}
          {nextRank.min - stats.unique_puzzles === 1 ? "puzzle" : "puzzles"} to
          <strong>{nextRank.name}</strong>
        </div>
      {:else}
        <div class="rank-next">You've reached the highest rank!</div>
      {/if}
    </div>

    <!-- full timeline: desktop only -->
    <div class="timeline-track">
      {#each RANKS as r, i}
        {@const done = i < rankIndex}
        {@const current = i === rankIndex}

        {#if i > 0}
          <div class="timeline-line" class:done={i <= rankIndex}></div>
        {/if}

        <div class="timeline-node-wrap">
          <div class="timeline-node" class:done class:current>
            {#if done || current}
              <svg class="icon node-star"><use href="/icons.svg#star"></use></svg>
            {/if}
          </div>
          <div class="timeline-node-label" class:done class:current>
            {r.name}
          </div>
          {#if !compact}
            <div class="timeline-node-min" class:done class:current>
              {r.min === 0 ? "0" : r.min}+
            </div>
          {/if}
        </div>
      {/each}
    </div>


  </div>

  <!-- ── Stats grid ────────────────────────────────────────────────── -->
  <div class="stats-grid">
    <div class="stat-card">
      <div class="stat-value">{formatNumber(stats.unique_puzzles)}</div>
      <div class="stat-label">Unique Puzzles</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">{formatNumber(stats.total_rentals)}</div>
      <div class="stat-label">Total Rentals</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">{formatNumber(stats.total_pieces)}</div>
      <div class="stat-label">Total Pieces</div>
    </div>
  </div>

  <!-- ── Top lists ─────────────────────────────────────────────────── -->
  <div class="lists-grid">
    <div class="list-section">
      <div class="list-heading">Top Brands</div>
      {#if !stats.top_brands?.length}
        <p class="list-empty">No data yet</p>
      {:else}
        {#each stats.top_brands as brand}
          <div class="bar-row">
            <span class="bar-name" title={brand.name}>{brand.name}</span>
            <div class="bar-track">
              <div
                class="bar-fill"
                style="width: {Math.round((brand.count / maxBrandCount) * 100)}%"
              ></div>
            </div>
            <span class="bar-count">{brand.count}</span>
          </div>
        {/each}
      {/if}
    </div>

    <div class="list-section">
      <div class="list-heading">Top Categories</div>
      {#if !stats.top_categories?.length}
        <p class="list-empty">No data yet</p>
      {:else}
        {#each stats.top_categories as cat}
          <div class="bar-row">
            <span class="bar-name" title={cat.name}>{cat.name}</span>
            <div class="bar-track">
              <div
                class="bar-fill"
                style="width: {Math.round((cat.count / maxCatCount) * 100)}%"
              ></div>
            </div>
            <span class="bar-count">{cat.count}</span>
          </div>
        {/each}
      {/if}
    </div>
  </div>
</div>

<style type="text/less">
  .puzzle-stats {
    --stat-value-size: var(--font-size-xl);
  }

  .puzzle-stats.compact {
    --stat-value-size: var(--font-size-lg);
  }

  // ── Rank timeline ──────────────────────────────────────────────────
  .rank-timeline {
    background: var(--col-accent-2);
    color: var(--col-gray-100);
    border-radius: 8px;
    padding: var(--spacing);
    margin-bottom: var(--spacing-sm);

    .compact & {
      padding: var(--spacing-sm);
    }
  }

  .timeline-header {
    margin-bottom: var(--spacing);

    .compact & {
      margin-bottom: var(--spacing-sm);
    }
  }

  .rank-label {
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    opacity: 0.75;
    margin-bottom: var(--spacing-xs);
  }

  .rank-name {
    font-size: var(--font-size-xl);
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: var(--spacing-xs);

    .compact & {
      font-size: var(--font-size-lg);
    }
  }

  .rank-next {
    font-size: var(--font-size-sm);
    opacity: 0.8;
  }

  // ── Timeline track ─────────────────────────────────────────────────
  .timeline-track {
    display: flex;
    align-items: flex-start;
    margin-bottom: var(--spacing-sm);
  }

  .timeline-line {
    flex: 1;
    height: 2px;
    background: rgba(255, 255, 255, 0.25);
    margin-top: 10px; // vertically center with node circles
    transition: background 0.3s;

    &.done {
      background: rgba(255, 255, 255, 0.85);
    }
  }

  .timeline-node-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  .timeline-node {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.4);
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &.done {
      background: rgba(255, 255, 255, 0.85);
      border-color: rgba(255, 255, 255, 0.85);
    }

    &.current {
      background: var(--col-gray-100);
      border-color: var(--col-gray-100);
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
    }

    .compact & {
      width: 18px;
      height: 18px;
    }
  }

  .node-star {
    width: 0.7rem;
    height: 0.7rem;
    color: var(--col-accent-2);
  }

  .timeline-node-label {
    font-size: var(--font-size-xs);
    opacity: 0.45;
    text-align: center;
    max-width: 56px;
    line-height: 1.2;

    &.done, &.current {
      opacity: 1;
    }

    &.current {
      font-weight: 700;
    }

    .compact & {
      max-width: 44px;
      font-size: 0.6rem;
    }
  }

  .timeline-node-min {
    font-size: 0.6rem;
    opacity: 0.35;
    text-align: center;

    &.done, &.current {
      opacity: 0.65;
    }
  }

  // ── Stats grid ────────────────────────────────────────────────────
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--spacing-sm);
    margin-bottom: var(--spacing-sm);
  }

  .stat-card {
    background: var(--col-gray-98);
    border: 1px solid var(--col-gray-80);
    border-radius: 8px;
    padding: var(--spacing-sm);
    text-align: center;
  }

  .stat-value {
    font-size: var(--stat-value-size);
    font-weight: 700;
    color: var(--col-accent-2);
    line-height: 1.2;
  }

  .stat-label {
    font-size: var(--font-size-xs);
    color: var(--col-gray-40);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: var(--spacing-xs);
  }

  // ── Top lists ─────────────────────────────────────────────────────
  .lists-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing);
  }

  .list-heading {
    font-size: var(--font-size-sm);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--col-gray-50);
    margin-bottom: var(--spacing-sm);
    font-weight: 600;
  }

  .list-empty {
    font-size: var(--font-size-sm);
    color: var(--col-gray-60);
    font-style: italic;
  }

  .bar-row {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    margin-bottom: var(--spacing-xs);
  }

  .bar-name {
    font-size: var(--font-size-sm);
    width: 100px;
    flex-shrink: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .bar-track {
    flex: 1;
    height: 6px;
    background: var(--col-gray-90);
    border-radius: 99px;
    overflow: hidden;
  }

  .bar-fill {
    height: 100%;
    background: var(--col-accent-1-d);
    border-radius: 99px;
  }

  .bar-count {
    font-size: var(--font-size-xs);
    color: var(--col-gray-50);
    min-width: 24px;
    text-align: right;
    flex-shrink: 0;
  }
</style>
